import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Col, Input, Button, Form, Modal, Select, Typography } from "antd";
import { Controller, useForm } from "react-hook-form";
import { SendOutlined, PlayCircleOutlined, PictureOutlined } from "@ant-design/icons";
import "./Styles.css";
import axios from "axios";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

export default function CreateExercise(props) {
  const { handleSubmit, control } = useForm();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [message, setMessage] = useState("");
  const { t } = useTranslation("Exercises");

  const [selectedExerciseCategory, setSelectedExerciseCategory] = useState(null);
  const [selectedFitnessLevel, setSelectedFitnessLevel] = useState(null);
  const [exerciseStatus, setExerciseStatus] = useState("active");

  // ? library from backend
  const [videos, setVideos] = useState([]); // [{key,url}]
  const [images, setImages] = useState([]); // [{key,url}]

  // selection
  const [selectedVideo, setSelectedVideo] = useState(null); // {key,url}
  const [selectedImage, setSelectedImage] = useState(null); // {key,url}

  // search
  const [videoSearch, setVideoSearch] = useState("");
  const [imageSearch, setImageSearch] = useState("");

  // preview
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);

  const categoryTranslation = {
    Aerobic: "A�robic",
    Strength: "Force",
    Endurance: "Endurance",
    Flexibility: "Flexibilit�",
    Balance: "�quilibre",
  };

  const fitnessLevelTranslation = {
    Easy: "Facile",
    Intermediate: "Interm�diaire",
    Advanced: "Avanc�",
  };

  const { token } = useToken();

  function openModal(msg, isError) {
    setMessage(msg);
    setIsErrorMessage(isError);
    setIsOpenModal(true);
  }

  function closeModal() {
    setIsOpenModal(false);
    setMessage("");
    setIsErrorMessage(false);
  }

  // ? Load library once
  useEffect(() => {
    if (!token) return;

    axios
      .get(`${Constants.SERVER_URL}/api/minio/library?limit=500`, {
        headers: { Authorization: "Bearer " + token },
      })
      .then((res) => {
        setVideos(res.data.videos || []);
        setImages(res.data.images || []);
      })
      .catch(() => {
        setVideos([]);
        setImages([]);
      });
  }, [token]);

  const filteredVideos = useMemo(() => {
    const q = videoSearch.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => v.key.toLowerCase().includes(q));
  }, [videos, videoSearch]);

  const filteredImages = useMemo(() => {
    const q = imageSearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((i) => i.key.toLowerCase().includes(q));
  }, [images, imageSearch]);

  const onSubmit = (data) => {
    if (!selectedVideo) {
      openModal("Veuillez s�lectionner une vid�o (MinIO).", true);
      return;
    }
    if (!selectedImage) {
      openModal("Veuillez s�lectionner une image (MinIO).", true);
      return;
    }

    const categoryInFrench =
      categoryTranslation[selectedExerciseCategory] || selectedExerciseCategory;
    const fitnessLevelInFrench =
      fitnessLevelTranslation[selectedFitnessLevel] || selectedFitnessLevel;

    const payload = {
      name: data.name,
      description: data.description,
      category: categoryInFrench,
      fitnessLevel: fitnessLevelInFrench,
      videoUrl: selectedVideo.key,   // store key only
      imageKey: selectedImage.key,   // store key only
      status: exerciseStatus,
      numberOfRepetitions: Number(data.numberOfRepetitions ?? 0),
    };

    axios
      .post(`${Constants.SERVER_URL}/create-exercise`, payload, {
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
      })
     .then(async (res) => {
  await props.refetchExercises();
  props.goBack();
})
      .catch((err) => {
        const errorMessage = err.response
          ? t(`Backend${err.response.data.message}`)
          : t("error_unknown");
        openModal(errorMessage, true);
      });
  };

  return (
    <div className="form-wrapper-exercice">
      <div className="title-container">
        <h2 className="form-title">{t("title_create_exercise")}</h2>
      </div>

      <div className="form-box-exercice">
        <Col span={12}>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <Form.Item label={t("label_exercise_name")} className="input-element">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input {...field} placeholder={t("placeholder_exercise_name")} required />
                )}
              />
            </Form.Item>

            <Form.Item label={t("label_exercise_category")} className="input-element">
              <Select
                value={selectedExerciseCategory}
                onChange={setSelectedExerciseCategory}
                placeholder={t("placeholder_exercise_category")}
                style={{ width: "100%" }}
                allowClear
              >
                {[
                  t("option_aerobic"),
                  t("option_strength"),
                  t("option_endurance"),
                  t("option_flexibility"),
                  t("option_balance"),
                ].map((category) => (
                  <Select.Option key={category} value={category}>
                    {category}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("label_expected_fitness_level")} className="input-element">
              <Select
                value={selectedFitnessLevel}
                onChange={setSelectedFitnessLevel}
                placeholder={t("placeholder_fitness_level")}
                style={{ width: "100%" }}
                allowClear
              >
                {[
                  t("option_easy"),
                  t("option_intermediate"),
                  t("option_advanced"),
                ].map((level) => (
                  <Select.Option key={level} value={level}>
                    {level}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("label_exercise_description")} className="input-element">
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Input.TextArea {...field} rows={4} placeholder={t("placeholder_exercise_description")} />
                )}
              />
            </Form.Item>

            <Form.Item label={t("label_number_of_reps")} className="input-element">
              <Controller
                name="numberOfRepetitions"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    value={field.value ?? 0}
                    placeholder={t("placeholder_number_of_reps")}
                    required
                  />
                )}
              />
            </Form.Item>

            <Form.Item label={t("label_exercise_status")} className="input-element">
              <Select value={exerciseStatus} onChange={setExerciseStatus} style={{ width: "100%" }}>
                <Select.Option value="active">{t("option_active")}</Select.Option>
                <Select.Option value="inactive">{t("option_inactive")}</Select.Option>
              </Select>
            </Form.Item>

            {/* ? VIDEO PICKER */}
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>
                <PlayCircleOutlined /> Choisir une video (MinIO)
              </Typography.Text>

              <Input
                style={{ marginTop: 8, marginBottom: 8 }}
                placeholder="Rechercher une video..."
                value={videoSearch}
                onChange={(e) => setVideoSearch(e.target.value)}
              />

              <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #eee", borderRadius: 8 }}>
                {filteredVideos.map((v) => (
                  <div
                    key={v.key}
                    onClick={() => setSelectedVideo(v)}
                    style={{
                      padding: 10,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f3f3f3",
                      background: selectedVideo?.key === v.key ? "#e6f4ff" : "white",
                    }}
                  >
                    <span>{v.key}</span>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setSelectedVideo(v); setIsVideoPreviewOpen(true); }}>
                      Apercu
                    </Button>
                  </div>
                ))}
                {!filteredVideos.length && <div style={{ padding: 10, color: "#999" }}>Aucune video</div>}
              </div>
            </div>

            {/* ? IMAGE PICKER */}
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>
                <PictureOutlined /> Choisir une image (MinIO)
              </Typography.Text>

              <Input
                style={{ marginTop: 8, marginBottom: 8 }}
                placeholder="Rechercher une image..."
                value={imageSearch}
                onChange={(e) => setImageSearch(e.target.value)}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  maxHeight: 260,
                  overflow: "auto",
                  padding: 10,
                  border: "1px solid #eee",
                  borderRadius: 8,
                }}
              >
                {filteredImages.map((img) => (
                  <div
                    key={img.key}
                    onClick={() => setSelectedImage(img)}
                    style={{
                      border: selectedImage?.key === img.key ? "2px solid #1677ff" : "1px solid #ddd",
                      borderRadius: 10,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "white",
                    }}
                    title={img.key}
                  >
                    <img
                      src={img.url}
                      alt={img.key}
                      style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }}
                    />
                    <div style={{ padding: 6, fontSize: 11, color: "#555" }}>
                      {img.key.replace("images/", "")}
                    </div>
                  </div>
                ))}
                {!filteredImages.length && <div style={{ color: "#999" }}>Aucune image</div>}
              </div>
            </div>

            <Form.Item className="input-element">
              <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                {t("button_submit")}
              </Button>
            </Form.Item>
          </Form>

          {/* Video preview modal */}
          <Modal
            open={isVideoPreviewOpen}
            onCancel={() => setIsVideoPreviewOpen(false)}
            footer={null}
            width={800}
            title="Aper�u vid�o"
          >
            {selectedVideo?.url ? (
              <ReactPlayer url={selectedVideo.url} controls width="100%" />
            ) : (
              <p>Aucune vid�o s�lectionn�e.</p>
            )}
          </Modal>

          {/* Feedback modal */}
          {isOpenModal && (
            <Modal
              open={isOpenModal}
              onCancel={closeModal}
              footer={[
                <Button key="close" onClick={closeModal}>
                  {t("button_close")}
                </Button>,
              ]}
            >
              <p style={{ color: isErrorMessage ? "red" : "black" }}>{message}</p>
            </Modal>
          )}
        </Col>
      </div>
    </div>
  );
}

CreateExercise.propTypes = {
  refetchExercises: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
};